import { Box, Link, StatusLabel } from '@factorialco/deprecated-design-system'
import { BankingValueObjectsTransferStatusEnum } from 'generated/resources/graphql'

import Sidepanel from 'modules/core/components/Sidepanel'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import { dateBackendDateTimeToUI } from 'modules/core/lib/dates'
import i18n from 'modules/core/lib/i18n'

import CustomTwoColumnsList from 'modules/attendance/components/CustomTwoColumnsList'
import { statusLabelType, statusText } from 'modules/banking/lib/mappers'
import AmountWithCurrency from 'modules/expenses/lib/transactions/AmountWithCurrency'

const getRejectionReasonLabel = (reason: string | null | undefined): string => {
  if (!reason) return '-'

  const reasonMap: Record<string, string> = {
    debtor_error: i18n.t('banking.transfers.rejectionReasons.debtorError'),
    creditor_error: i18n.t('banking.transfers.rejectionReasons.creditorError'),
    card_error: i18n.t('banking.transfers.rejectionReasons.cardError'),
    factorial_error: i18n.t('banking.transfers.rejectionReasons.factorialError'),
    transfer_error: i18n.t('banking.transfers.rejectionReasons.transferError'),
    provider_error: i18n.t('banking.transfers.rejectionReasons.providerError'),
    unknown_error: i18n.t('banking.transfers.rejectionReasons.unknownError'),
    technical_error: i18n.t('banking.transfers.rejectionReasons.technicalError'),
    rejected_consent: i18n.t('banking.transfers.rejectionReasons.rejectedConsent'),
  }
  return reasonMap[reason] ?? reason
}

export interface TransferData {
  [key: string]: unknown
  id: number
  accountNumber: string
  amountCents: number
  beneficiaryName: string
  createdAt: string
  currency: string
  dueDate?: string | null
  label: string
  paymentRequestItem?: {
    id: number
    paymentRequest?: {
      id: number
      label: string
    } | null
  } | null
  rejectionReason?: string | null
  status: BankingValueObjectsTransferStatusEnum
}

interface TransferDetailSidePanelProps {
  transfer: TransferData
  onClose: () => void
}

export const TransferDetailSidePanel = ({ transfer, onClose }: TransferDetailSidePanelProps) => {
  const { navigation } = useNavigation()

  const isRejected = transfer.status === 'rejected'
  const paymentRequest = transfer.paymentRequestItem?.paymentRequest

  const listItems = [
    {
      title: i18n.t('banking.attributes.status'),
      info: (
        <StatusLabel type={statusLabelType(transfer.status)}>
          {statusText(transfer.status)}
        </StatusLabel>
      ),
    },
    {
      title: i18n.t('banking.attributes.amount'),
      info: <AmountWithCurrency amount={transfer.amountCents} currency={transfer.currency} />,
    },
    {
      title: i18n.t('banking.attributes.beneficiary'),
      info: transfer.beneficiaryName,
    },
    {
      title: i18n.t('banking.attributes.account_number'),
      info: transfer.accountNumber,
    },
    {
      title: i18n.t('banking.attributes.created_at'),
      info: dateBackendDateTimeToUI(transfer.createdAt),
    },
    ...(transfer.dueDate
      ? [
          {
            title: i18n.t('banking.attributes.due_date'),
            info: dateBackendDateTimeToUI(transfer.dueDate),
          },
        ]
      : []),
    ...(isRejected
      ? [
          {
            title: i18n.t('banking.transfers.rejectionReason'),
            info: getRejectionReasonLabel(transfer.rejectionReason),
          },
        ]
      : []),
    ...(paymentRequest
      ? [
          {
            title: i18n.t('banking.attributes.payment_request'),
            info: (
              <Link.Router
                to={navigation.finance.treasury.banking.paymentRequests.paymentRequestId.fullPath({
                  paymentRequestId: paymentRequest.id,
                })}
              >
                {paymentRequest.label}
              </Link.Router>
            ),
          },
        ]
      : []),
  ]

  return (
    <Sidepanel
      overlay
      boxPadding
      width="wide"
      title={transfer.label || transfer.beneficiaryName}
      open
      onClose={onClose}
      headerBorder
      closeLabel={i18n.t('common.close')}
    >
      <Box width="full" gap="s24">
        <CustomTwoColumnsList list={listItems} />
      </Box>
    </Sidepanel>
  )
}
